import { partBalancesQuery } from "@common/api/part-balances"
import {
  partStockGroupPages,
  partStockQuery,
  partStocksInfiniteQuery,
} from "@common/api/part-stock"
import {
  useRestorePartMutation,
  useUpdatePartMutation,
} from "@common/api/parts"
import { pagedRows } from "@common/api/utils"
import type {
  PartArchiveScope,
  PartPublic,
  PartStockDetailPublic,
} from "@common/client"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { Form } from "@common/components/ui/form"
import { useEscape } from "@common/hooks/use-escape"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import {
  DetailPane,
  DetailPanelActions,
  DetailPanelFields,
  PaneMenu,
} from "@/components/layout/detail-panel"
import {
  type GroupOrder,
  useGroupPages,
} from "@/components/layout/use-group-paging"
import {
  PaneSections,
  PaneStepper,
  PaneTitleField,
} from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import ArchiveOutline from "~icons/material-symbols/archive-outline"
import UnarchiveOutline from "~icons/material-symbols/unarchive-outline"
import {
  PartChips,
  PartControls,
  PartDetailsFields,
  PartHeading,
  PartPricesSection,
  PartQuotesSection,
  PartStockSection,
  PartSummary,
  PartWorkOrdersSection,
} from "../-components/part-panel"
import {
  type PartFormInput,
  partDefaults,
  partSchema,
  partUpdatePayload,
} from "../-lib/part-form"
import { displayOf, stockParams } from "../-lib/search"

export const Route = createFileRoute("/_layout/inventory/$partId")({
  staticData: { detailSidebar: true },
  loaderDeps: ({ search }) => ({ archive: archiveScope(search.tab) }),
  loader: ({ context: { queryClient }, params: { partId }, deps }) =>
    Promise.all([
      queryClient.ensureQueryData(
        partStockQuery({ part_id: partId, archive: deps.archive }),
      ),
      queryClient.ensureQueryData(partBalancesQuery(partId)),
    ]),
  component: PartPanel,
})

type StepOrder = GroupOrder<PartStockDetailPublic> & { total: number }

function PartPanel(): React.ReactElement {
  const search = Route.useSearch()
  return displayOf(search).group ? <GroupedPartPanel /> : <ListedPartPanel />
}

function ListedPartPanel(): React.ReactElement {
  const search = Route.useSearch()
  const { data, hasNextPage, fetchNextPage } = useSuspenseInfiniteQuery(
    partStocksInfiniteQuery(stockParams(search)),
  )
  const rows = pagedRows(data.pages, (stock) => stock.part_id)
  const indexOf = (partId: string) =>
    rows.findIndex((stock) => stock.part_id === partId)
  const order: StepOrder = {
    total: data.pages[0]?.count ?? rows.length,
    indexOf,
    previous: (partId) => {
      const index = indexOf(partId)
      return index > 0 ? rows[index - 1] : undefined
    },
    next: async (partId) => {
      const index = indexOf(partId)
      if (index === -1) return undefined
      const loaded = rows[index + 1]
      if (loaded || !hasNextPage) return loaded
      const fetched = await fetchNextPage()
      const pages = fetched.data?.pages ?? []
      return pagedRows(pages, (stock) => stock.part_id)[index + 1]
    },
  }
  return <PartPane order={order} />
}

function GroupedPartPanel(): React.ReactElement {
  const search = Route.useSearch()
  const { data, order } = useGroupPages(
    partStockGroupPages,
    stockParams(search),
    (stock) => stock.part_id,
  )
  return <PartPane order={{ ...order, total: data.count }} />
}

function PartPane({ order }: { order: StepOrder }): React.ReactElement {
  const { partId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { data: stock } = useSuspenseQuery(
    partStockQuery({ part_id: partId, archive: archiveScope(tab) }),
  )
  const navigate = useNavigate()
  const close = () => navigate({ to: "/inventory", search: true })
  useEscape(close)
  const index = order.indexOf(partId)
  const open = (target: PartStockDetailPublic | undefined) => {
    if (target) {
      void navigate({
        to: "/inventory/$partId",
        params: { partId: target.part_id },
        search: true,
      })
    }
  }

  return (
    <DetailPane
      title={stock.part?.number ?? "Part"}
      heading={<PartHeading stock={stock} />}
      actions={
        <>
          <PaneStepper
            noun="part"
            index={index}
            total={order.total}
            onPrevious={index > 0 ? () => open(order.previous(partId)) : null}
            onNext={
              index !== -1 && index < order.total - 1
                ? () => void order.next(partId).then(open)
                : null
            }
          />
          <PartMenu stock={stock} />
        </>
      }
      onClose={close}
    >
      {stock.part ? (
        <PartForm
          key={`${partId}-${stock.archived}`}
          stock={stock}
          part={stock.part}
        />
      ) : (
        <DetailPanelFields>
          <PartSummary stock={stock} />
        </DetailPanelFields>
      )}
      <Outlet />
    </DetailPane>
  )
}

function PartForm({
  stock,
  part,
}: {
  stock: PartStockDetailPublic
  part: PartPublic
}): React.ReactElement {
  const mutation = useUpdatePartMutation()
  const showToast = useCustomToast()
  const canUpdate = useHasPermission("part:update") && !stock.archived
  const form = useSharedForm<PartFormInput>({
    resolver: zodResolver(partSchema),
    defaultValues: partDefaults(part),
    criteriaMode: "all",
    mode: "onBlur",
  })

  const submit = (values: PartFormInput) =>
    mutation.mutate(
      { part_id: part.id, partUpdate: partUpdatePayload(values) },
      {
        onSuccess: (saved) => {
          form.reset(partDefaults(saved))
          showToast("Saved", `${saved.number} updated.`, "success")
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(submit)}
        className="flex h-full flex-col"
      >
        <DetailPanelFields>
          <PaneSections
            header={
              <PartSummary
                stock={stock}
                title={
                  canUpdate ? (
                    <PaneTitleField<PartFormInput>
                      name="description"
                      label="Description"
                      placeholder="Add a description"
                      editable
                      optional
                    />
                  ) : undefined
                }
              >
                <PartChips
                  part={part}
                  editable={canUpdate}
                  stocked={Number(stock.on_hand) > 0}
                />
              </PartSummary>
            }
          >
            <PartDetailsFields draft={false} editable={canUpdate} />
            <PartControls editable={canUpdate} part={part} />
            <PartStockSection part={part} editable={!stock.archived} />
            <PartQuotesSection partId={part.id} />
            <PartWorkOrdersSection partId={part.id} />
            <PartPricesSection partId={part.id} editable={!stock.archived} />
          </PaneSections>
        </DetailPanelFields>
        <DetailPanelActions
          dirty={canUpdate && form.formState.isDirty}
          pending={mutation.isPending}
          onReset={() => form.reset()}
        >
          {null}
        </DetailPanelActions>
      </form>
    </Form>
  )
}

function archiveScope(tab: string | undefined): PartArchiveScope {
  return tab === "archived" ? "archived" : "live"
}

function PartMenu({
  stock,
}: {
  stock: PartStockDetailPublic
}): React.ReactNode {
  const canDelete = useHasPermission("part:delete")
  const canRestore = useHasPermission("part:update")
  const restore = useRestorePartMutation()
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const number = stock.part?.number ?? "Part"

  const restoreAction = canRestore && (
    <DropdownMenuItem
      disabled={restore.isPending}
      onSelect={() =>
        restore.mutate(
          { part_id: stock.part_id },
          {
            onSuccess: () => {
              showToast("Restored", `${number} restored.`, "success")
              navigate({
                to: "/inventory/$partId",
                params: { partId: stock.part_id },
                search: (previous) => ({ ...previous, tab: undefined }),
              })
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    >
      <UnarchiveOutline />
      Restore
    </DropdownMenuItem>
  )
  const archive = canDelete && (
    <DropdownMenuItem asChild>
      <Link
        to="/inventory/$partId/archive"
        params={{ partId: stock.part_id }}
        search={true}
      >
        <ArchiveOutline />
        Archive
      </Link>
    </DropdownMenuItem>
  )
  const action = stock.archived ? restoreAction : archive

  return <PaneMenu label="Part actions">{stock.part && action}</PaneMenu>
}
